import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { defer, forkJoin, map, mergeMap, Observable, retry, shareReplay, Subject, throwError, timer } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { environment } from '../../../environment/environment';
import { WebSocketMessage, WebSocketMessageType } from '../types/websocket.types';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { MsalService } from '@azure/msal-angular';

@Injectable({ providedIn: 'root' })
export class WebsocketService implements OnDestroy {
  private static readonly RECONNECT_DELAY_MS = 30_000;

  private readonly http = inject(HttpClient);
  private readonly _authService = inject(MsalService);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  private connection: WebSocketSubject<WebSocketMessage> | null = null;
  private readonly messageSubjects = new Map<WebSocketMessageType, Subject<unknown>>();
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  // Tracks whether the current connection has already burned its one-shot
  // silent-refresh budget. Reset on every successful reconnection so the next
  // token expiry (~60 min later) gets the same grace period.
  private hasAttemptedReauth = false;

  private readonly API_URL = `${environment.apiUrl}/websocket/address`;
  private readonly WS_APP_NAME = environment.websocket.appName;
  private readonly _connected = signal(false);
  readonly connected = this._connected.asReadonly();

  constructor() {
    void this.initConnection();
    this.destroyRef.onDestroy(() => {
      if (this.reconnectTimeoutId) clearTimeout(this.reconnectTimeoutId);
      this.connection?.complete();
      this.messageSubjects.forEach(subject => subject.complete());
    });
  }

  private async initConnection(): Promise<void> {
    if (!this.resolveActiveAccount()) return;
    this.connect();
  }

  // Returns an MSAL account ready for `acquireTokenSilent` or null if the user
  // is in bypass mode, signed-out, or pre-login. Caller must skip the WebSocket
  // handshake when null is returned; `acquireTokenSilent` without an active
  // account throws `BrowserAuthError: no_account_error`, which would bubble up
  // to the global error handler.
  private resolveActiveAccount(): AccountInfo | null {
    const instance = this._authService.instance;
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
    if (account) instance.setActiveAccount(account);
    return account;
  }

  /** 1) Fetch once; reuse everywhere */
  readonly apiUrl$: Observable<string> = this.http.get(this.API_URL, { responseType: 'text' }).pipe(
    map(url => url.trim()),
    // Cache the single successful emission; subsequent subscribers reuse it
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private connect(): void {
    // `defer` makes the whole token+socket chain re-run on every retry, so each
    // reconnection handshake carries a FRESH token instead of replaying the one
    // frozen into the URL at first connect - the cause of the recurring
    // "Signature expired" loop once the baked token's ~60min lifetime lapsed.
    // The first attempt rides MSAL's fast cache; retries force a server refresh
    // so a clock-skewed or suspend/resumed cache can't keep handing back a token
    // the server already treats as expired.
    let attempt = 0;
    defer(() =>
      forkJoin([
        this._authService.acquireTokenSilent({ scopes: environment.MSAL.scopes, forceRefresh: attempt > 0 }),
        this.apiUrl$,
      ]).pipe(
        mergeMap(([respAuth, respURL]) => {
          const url = `${respURL}?idp=azure&application=${this.WS_APP_NAME}&token=${respAuth.accessToken}`;
          this.connection = webSocket<WebSocketMessage>({
            url,
            openObserver: {
              next: () => {
                attempt = 0;
                this.onConnectionRestored();
              },
            },
            closeObserver: { next: () => this._connected.set(false) },
          });
          return this.connection;
        }),
      ),
    )
      .pipe(
        retry({
          count: environment.websocket.reconnectAttempts,
          // Refill the budget on every received message so the count governs
          // CONSECUTIVE failures (the intended fast backoff), not lifetime-
          // cumulative drops - which would otherwise silently degrade a flaky
          // long-lived socket from the 5s retry path to the 30s reconnect path.
          resetOnSuccess: true,
          delay: (err: unknown) => {
            attempt += 1;
            // An interaction-required failure can't heal by retrying the same
            // way (Firefox Total Cookie Protection blocking silent renewal once
            // the refresh token lapses); surface it at once so the one-shot
            // reauth - and eventual Entra redirect - takes over rather than spinning.
            if (err instanceof InteractionRequiredAuthError) {
              return throwError(() => err);
            }
            return timer(environment.websocket.reconnectInterval * 1000);
          },
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: msg => this.handleMessage(msg),
        // Reached when the retry budget is exhausted (transient drop -> reconnect)
        // or an interaction-required failure short-circuited the loop (-> reauth,
        // then Entra redirect). Without this the outer stream would die uncaught AND skip
        // `complete`, stranding the root-singleton socket until a page reload.
        error: (err: unknown) => this.handleConnectError(err),
        complete: () => {
          this.connection = null;
          this.scheduleReconnect();
        },
      });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeoutId) return;
    this.reconnectTimeoutId = setTimeout(() => {
      this.reconnectTimeoutId = null;
      if (!this.resolveActiveAccount()) return;
      this.connect();
    }, WebsocketService.RECONNECT_DELAY_MS);
  }

  private handleMessage(msg: WebSocketMessage): void {
    const subject = this.messageSubjects.get(msg.type);
    if (subject) {
      subject.next(msg.message);
    }

    if (msg.type === WebSocketMessageType.MESSAGE) {
      this.toast.info(String(msg.message), false, msg.title);
    }
  }

  // Classifies a failure of the connect() source stream (acquireTokenSilent +
  // address fetch). An interaction-required failure means MSAL cannot renew the
  // token silently - the Firefox Total Cookie Protection case once the refresh
  // token lapses - so the session needs interactive auth: route it through the
  // one-shot reauth (which escalates to an Entra redirect). Anything else is transient and
  // simply earns a reconnect.
  private handleConnectError(err: unknown): void {
    if (err instanceof InteractionRequiredAuthError) {
      this.handleAuthError();
    } else {
      this.scheduleReconnect();
    }
  }

  private handleAuthError(): void {
    // Reached when acquireTokenSilent reports the session needs interactive
    // auth (e.g. Firefox's Total Cookie Protection blocks MSAL's silent iframe
    // renewal once the refresh token lapses). Burn one reconnect first:
    // `connect()` re-runs `acquireTokenSilent` and may yet mint a fresh token.
    // Only when that retry is ALSO interaction-required do we conclude the
    // silent path is exhausted and send the user through Entra, which keeps
    // its sign-in cookie where a logout would end it.
    if (this.hasAttemptedReauth) {
      this.authService.reauthenticate();
      return;
    }
    this.hasAttemptedReauth = true;
    this.closeConnection();
    this.connect();
  }

  private onConnectionRestored(): void {
    this._connected.set(true);
    // Successful (re)connection = the credentials worked. Refill the silent-
    // refresh budget so the next token expiry, hours from now, gets its own
    // chance before we redirect.
    this.hasAttemptedReauth = false;
  }

  getObservable<T = unknown>(type: WebSocketMessageType): Observable<T> {
    let subject = this.messageSubjects.get(type);
    if (!subject) {
      subject = new Subject<unknown>();
      this.messageSubjects.set(type, subject);
    }
    return subject.asObservable() as Observable<T>;
  }

  ngOnDestroy() {
    this.closeConnection();
  }

  closeConnection() {
    if (this.connection) {
      this.connection.complete();
      this.connection = null;
    }
  }

  isConnected(): boolean {
    return this._connected();
  }
}
